import React, { createContext, useContext, useState, useEffect } from "react";

const translations = {
  fr: {
    nav: { home: "Accueil", sell: "Vendre", buy: "Acheter", rent: "Louer", about: "À propos", contact: "Contact", admin: "Admin" },
    cta: {
      private_evaluation: "Évaluation privée",
      view_listings: "Voir les inscriptions",
      private_consultation: "Consultation privée",
      start_search: "Débuter ma recherche",
      confidential_evaluation: "Évaluation confidentielle",
      send: "Envoyer",
      request: "Demander",
    },
    hero: {
      eyebrow: "Courtier immobilier, résidentiel et commercial — Grand Montréal",
      title: "L'immobilier, élevé.",
      subtitle: "Représentation stratégique pour acheteurs et vendeurs dans le Grand Montréal.",
    },
    approach: {
      eyebrow: "Philosophie",
      title: "Une approche discrète. Des résultats mesurables.",
      body: "Chaque mandat est traité avec la confidentialité, la précision et l'exigence qu'exige votre patrimoine. Pas de bruit. Seulement des résultats.",
    },
    pillars: {
      eyebrow: "Ce qui me distingue",
      title: "Trois piliers, une exécution sans compromis.",
      items: [
        { n: "01", t: "Excellence marketing", d: "Photographie, vidéo cinématographique et positionnement éditorial pensés pour le segment haut de gamme." },
        { n: "02", t: "Réseau qualifié", d: "Accès à un cercle privé d'acheteurs qualifiés et d'investisseurs." },
        { n: "03", t: "Négociation stratégique", d: "Une approche méthodique de la négociation pour protéger votre valeur — à chaque étape." },
      ],
    },
    video: { eyebrow: "Film", title_prefix: "La signature", title_signature: "Roland Azar" },
    career: {
      eyebrow: "17 ans de carrière",
      title: "Dix-sept ans à façonner des transactions d'exception.",
      p1: "Depuis près de deux décennies, Roland Azar accompagne une clientèle exigeante dans les moments les plus stratégiques de leur patrimoine immobilier. Dix-sept années passées à maîtriser les nuances du marché du Grand Montréal — des penthouses du Vieux-Montréal aux résidences de prestige d'Outremont, des duplex architecturaux du Plateau aux immeubles commerciaux qui définissent la ville.",
      pullquote: "Ce n'est pas une carrière. C'est une signature.",
      p2: "Chaque mandat, résidentiel comme commercial, est traité avec la même rigueur : une analyse fine, une stratégie de positionnement sur mesure, et une négociation menée avec la précision d'un professionnel qui connaît la valeur de chaque détail. Derrière chaque transaction, un engagement constant envers ses clients — discrétion absolue, transparence totale, résultats mesurables.",
      p3: "Dix-sept ans d'expérience. Des centaines de familles accompagnées. Des millions en transactions réalisées. Et une conviction inchangée : l'immobilier d'exception mérite une représentation d'exception.",
    },
    featured: { eyebrow: "Propriétés choisies", title: "Une sélection discrète.", view: "Voir la propriété" },
    sold: { eyebrow: "Récemment vendu", title: "Résultats, livrés.", sold_tag: "Vendu" },
    testimonials: {
      eyebrow: "Témoignages", title: "Ce que disent les clients.",
      items: [
        { q: "Un professionnalisme rare. Notre condo s'est vendu au-dessus du prix demandé en moins de deux semaines.", a: "S. & M. — Westmount" },
        { q: "Roland a été discret, stratégique et extrêmement efficace. Une expérience irréprochable.", a: "J. Tremblay — Vieux-Montréal" },
        { q: "Il comprend parfaitement le segment haut de gamme et livre ce qu'il promet.", a: "A. Laurent — Outremont" },
      ],
    },
    finalCta: { title: "Prêt à parler de votre prochaine étape ?", sub: "Consultation confidentielle, sans engagement.", btn: "Planifier un entretien" },
    sell: {
      eyebrow: "Vendre",
      title: "Maximiser la valeur de votre propriété.",
      intro: "Un positionnement éditorial, un marketing cinématographique et un réseau d'acheteurs qualifiés — tout ce qu'il faut pour obtenir la meilleure offre, dans les meilleurs délais.",
      strategy_title: "Stratégie de marketing premium",
      strategy_body: "Photographie architecturale, vidéo 4K, visite virtuelle, mise en scène, plan média ciblé et diffusion dans nos canaux privés.",
      steps_title: "Processus en cinq temps",
      steps: [
        { n: "01", t: "Évaluation", d: "Analyse comparative, positionnement prix, stratégie de mise en marché." },
        { n: "02", t: "Préparation", d: "Mise en scène, photographie, vidéo cinématographique et contenu éditorial." },
        { n: "03", t: "Lancement", d: "Diffusion ciblée, cercle privé d'acheteurs, médias et plateformes premium." },
        { n: "04", t: "Négociation", d: "Représentation stratégique pour maximiser votre valeur." },
        { n: "05", t: "Clôture", d: "Accompagnement jusqu'à la signature et au-delà." },
      ],
      cta: "Obtenir votre évaluation confidentielle",
    },
    buy: {
      eyebrow: "Acheter",
      title: "Des propriétés à la hauteur de vos standards.",
      intro: "Accédez à des opportunités exclusives, incluant des inscriptions hors-marché, avec un accompagnement sur mesure.",
      search_title: "Recherche privée",
      search_type: "Type", search_neigh: "Quartier", search_budget: "Budget", search_any: "Indifférent",
      types: { condo: "Condo", duplex: "Duplex", house: "Maison", penthouse: "Penthouse" },
      cta: "Débuter ma recherche",
    },
    rent: {
      eyebrow: "Louer",
      title: "Résidence. Bureaux. Court ou long terme.",
      intro: "Que vous cherchiez un condo signature à Montréal, un espace commercial à Laval, ou une résidence exécutive pour une relocation, Roland Azar vous propose un service de location haut de gamme, discret et sur mesure.",
      pillars_title: "Pour qui ?",
      pillars: [
        { n: "01", t: "Particuliers exigeants", d: "Accès à des appartements et maisons de qualité, dans les quartiers les plus recherchés du Grand Montréal." },
        { n: "02", t: "Cadres et relocations", d: "Solutions meublées ou non, baux flexibles, accompagnement complet pour les professionnels en déplacement." },
        { n: "03", t: "Entreprises et commerces", d: "Locations de bureaux, espaces commerciaux et immeubles à revenus, sélectionnés selon vos critères stratégiques." },
      ],
      how_title: "Comment ça fonctionne",
      steps: [
        { n: "01", t: "Consultation", d: "Nous définissons ensemble vos besoins, votre budget et vos priorités." },
        { n: "02", t: "Sélection ciblée", d: "Je vous présente uniquement les propriétés qui correspondent à vos exigences." },
        { n: "03", t: "Visites accompagnées", d: "Organisation des visites, analyse comparative, négociation des conditions du bail." },
        { n: "04", t: "Signature et emménagement", d: "Accompagnement complet jusqu'à la remise des clés — et au-delà." },
      ],
      cta_title: "Prêt à trouver votre prochain espace ?",
      cta: "Discuter de mes besoins",
    },
    about: {
      eyebrow: "À propos",
      title: "Roland Azar",
      role: "Courtier immobilier, résidentiel et commercial — L'Expert immobilier P.M.",
      body1: "Basé à Laval et actif dans l'ensemble du Grand Montréal, Roland Azar accompagne une clientèle exigeante dans l'acquisition et la vente de condos, duplex et propriétés de prestige.",
      body2: "Son approche combine transparence, stratégie et exécution méticuleuse. Chaque mandat est mené comme un projet unique — discret, précis, orienté résultats.",
      stats: [
        { n: "17+", l: "Années d'expérience" },
        { n: "Plusieurs", l: "Transactions réalisées" },
        { n: "98%", l: "Clients satisfaits" },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Demander une consultation privée.",
      intro: "Réponse sous 24 h ouvrables. Confidentialité garantie.",
      name: "Nom complet", email: "Courriel", phone: "Téléphone", message: "Message",
      sent: "Merci. Votre demande a été reçue.",
      error: "Une erreur est survenue. Veuillez réessayer.",
      office: "Bureau", phone_lbl: "Téléphone", email_lbl: "Courriel",
    },
    popup: {
      eyebrow: "Accès privé",
      title: "Propriétés hors-marché.",
      body: "Recevez en priorité les inscriptions confidentielles, avant leur mise en marché publique.",
      cta: "Obtenir l'accès",
      later: "Plus tard",
    },
    footer: {
      tag: "Courtier immobilier, résidentiel et commercial — Grand Montréal",
      rights: "Tous droits réservés.",
      links: "Navigation", contact: "Contact",
    },
    admin: {
      login_title: "Accès administrateur", login_cta: "Se connecter",
      dashboard: "Tableau de bord", leads: "Demandes", properties: "Propriétés",
      logout: "Déconnexion",
      status_new: "Nouveau", status_contacted: "Contacté", status_archived: "Archivé",
      add_property: "Ajouter une propriété", edit: "Modifier", delete: "Supprimer", save: "Enregistrer", cancel: "Annuler",
    },
  },
  en: {
    nav: { home: "Home", sell: "Sell", buy: "Buy", rent: "Rent", about: "About", contact: "Contact", admin: "Admin" },
    cta: {
      private_evaluation: "Private Evaluation",
      view_listings: "View Exclusive Listings",
      private_consultation: "Private Consultation",
      start_search: "Start Your Search",
      confidential_evaluation: "Confidential Evaluation",
      send: "Send",
      request: "Request",
    },
    hero: {
      eyebrow: "Real Estate, Residential & Commercial Broker — Greater Montreal",
      title: "Real Estate, Elevated.",
      subtitle: "Strategic representation for buyers and sellers in Greater Montreal.",
    },
    approach: {
      eyebrow: "Philosophy",
      title: "A discreet approach. Measurable results.",
      body: "Every mandate is handled with the confidentiality, precision and rigour your portfolio deserves. No noise. Only results.",
    },
    pillars: {
      eyebrow: "What sets me apart",
      title: "Three pillars, uncompromising execution.",
      items: [
        { n: "01", t: "Marketing Excellence", d: "Photography, cinematic video and editorial positioning built for the luxury segment." },
        { n: "02", t: "Qualified Network", d: "Access to a private circle of qualified buyers and investors." },
        { n: "03", t: "Strategic Negotiation", d: "A methodical negotiation approach to protect your value — at every step." },
      ],
    },
    video: { eyebrow: "Film", title_prefix: "The signature of", title_signature: "Roland Azar" },
    career: {
      eyebrow: "17 Years of Mastery",
      title: "Seventeen years crafting exceptional transactions.",
      p1: "For nearly two decades, Roland Azar has guided a discerning clientele through the most strategic moments of their real estate journey. Seventeen years refining a mastery of the Greater Montreal market — from the penthouses of Old Montreal to the prestigious residences of Outremont, from the architectural duplexes of the Plateau to the commercial buildings that shape the city.",
      pullquote: "This is not a career. It is a signature.",
      p2: "Every mandate — residential or commercial — is treated with the same rigour: precise analysis, a bespoke positioning strategy, and negotiation conducted with the precision of a professional who understands the value of every detail. Behind every transaction, an unwavering commitment to his clients — absolute discretion, complete transparency, measurable results.",
      p3: "Seventeen years of experience. Hundreds of families guided. Millions in transactions closed. And one conviction that has never changed: exceptional real estate deserves exceptional representation.",
    },
    featured: { eyebrow: "Curated properties", title: "A discreet selection.", view: "View property" },
    sold: { eyebrow: "Recently sold", title: "Results, delivered.", sold_tag: "Sold" },
    testimonials: {
      eyebrow: "Testimonials", title: "What clients say.",
      items: [
        { q: "Rare level of professionalism. Our condo sold above asking in under two weeks.", a: "S. & M. — Westmount" },
        { q: "Roland was discreet, strategic and extremely effective. A flawless experience.", a: "J. Tremblay — Old Montreal" },
        { q: "He fully understands the luxury segment and delivers on what he promises.", a: "A. Laurent — Outremont" },
      ],
    },
    finalCta: { title: "Ready to discuss your next step?", sub: "Confidential consultation, no obligation.", btn: "Schedule a meeting" },
    sell: {
      eyebrow: "Sell",
      title: "Maximize the Value of Your Property.",
      intro: "Editorial positioning, cinematic marketing and a qualified buyer network — everything needed to secure the best offer, in the best time frame.",
      strategy_title: "Premium marketing strategy",
      strategy_body: "Architectural photography, 4K video, virtual tour, staging, targeted media plan and distribution in our private channels.",
      steps_title: "A five-step process",
      steps: [
        { n: "01", t: "Evaluation", d: "Comparative analysis, price positioning, go-to-market strategy." },
        { n: "02", t: "Preparation", d: "Staging, photography, cinematic video and editorial content." },
        { n: "03", t: "Launch", d: "Targeted distribution, private buyer circle, premium platforms." },
        { n: "04", t: "Negotiation", d: "Strategic representation to maximize your value." },
        { n: "05", t: "Closing", d: "Guidance through signature — and beyond." },
      ],
      cta: "Get your confidential evaluation",
    },
    buy: {
      eyebrow: "Buy",
      title: "Access properties that match your standards.",
      intro: "Access exclusive opportunities, including off-market listings, with tailored guidance end-to-end.",
      search_title: "Private search",
      search_type: "Type", search_neigh: "Neighborhood", search_budget: "Budget", search_any: "Any",
      types: { condo: "Condo", duplex: "Duplex", house: "House", penthouse: "Penthouse" },
      cta: "Start your search",
    },
    rent: {
      eyebrow: "Rent",
      title: "Residential. Offices. Short or long term.",
      intro: "Whether you're looking for a signature condo in Montreal, a commercial space in Laval, or an executive residence for a relocation, Roland Azar offers a discreet, tailored high-end rental service.",
      pillars_title: "Who it's for",
      pillars: [
        { n: "01", t: "Discerning individuals", d: "Access to quality apartments and homes in the most sought-after neighborhoods of Greater Montreal." },
        { n: "02", t: "Executives & relocations", d: "Furnished or unfurnished solutions, flexible leases, complete support for professionals on the move." },
        { n: "03", t: "Businesses & retail", d: "Office leases, commercial spaces and income properties, selected to your strategic criteria." },
      ],
      how_title: "How it works",
      steps: [
        { n: "01", t: "Consultation", d: "We define together your needs, budget, and priorities." },
        { n: "02", t: "Targeted selection", d: "I present only properties that match your requirements." },
        { n: "03", t: "Accompanied visits", d: "Visit scheduling, comparative analysis, lease terms negotiation." },
        { n: "04", t: "Signing & move-in", d: "End-to-end guidance through handover — and beyond." },
      ],
      cta_title: "Ready to find your next space?",
      cta: "Discuss my needs",
    },
    about: {
      eyebrow: "About",
      title: "Roland Azar",
      role: "Real Estate, Residential & Commercial Broker — L'Expert immobilier P.M.",
      body1: "Based in Laval and active across Greater Montreal, Roland Azar guides a discerning clientele through the acquisition and sale of condos, duplexes and luxury properties.",
      body2: "His approach blends transparency, strategy and meticulous execution. Every mandate is treated as a unique project — discreet, precise, results-driven.",
      stats: [
        { n: "17+", l: "Years of experience" },
        { n: "Many", l: "Transactions closed" },
        { n: "98%", l: "Client satisfaction" },
      ],
    },
    contact: {
      eyebrow: "Contact",
      title: "Request a private consultation.",
      intro: "Response within 24 business hours. Confidentiality guaranteed.",
      name: "Full name", email: "Email", phone: "Phone", message: "Message",
      sent: "Thank you. Your request has been received.",
      error: "Something went wrong. Please try again.",
      office: "Office", phone_lbl: "Phone", email_lbl: "Email",
    },
    popup: {
      eyebrow: "Private access",
      title: "Off-market properties.",
      body: "Receive confidential listings in priority — before they hit the public market.",
      cta: "Get access",
      later: "Later",
    },
    footer: {
      tag: "Real Estate, Residential & Commercial Broker — Greater Montreal",
      rights: "All rights reserved.",
      links: "Navigation", contact: "Contact",
    },
    admin: {
      login_title: "Administrator access", login_cta: "Sign in",
      dashboard: "Dashboard", leads: "Leads", properties: "Properties",
      logout: "Sign out",
      status_new: "New", status_contacted: "Contacted", status_archived: "Archived",
      add_property: "Add property", edit: "Edit", delete: "Delete", save: "Save", cancel: "Cancel",
    },
  },
};

const LangContext = createContext({ lang: "fr", setLang: () => {}, t: translations.fr });

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("ra_lang") || "fr");
  useEffect(() => {
    localStorage.setItem("ra_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = { lang, setLang, t: translations[lang] };
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
